import AbstractAuthenticatedService from '@vertical-plus/vue-js-micro-service-components/lib/model/service/abstract-authenticated-service';
import Client from '@/model/entity/client/client';
import ClientSearchConditionsInterface from '@/model/vo/client/client-search-conditions-interface';
import ClientSearchResultsVo from '@/model/vo/client/client-search-results-vo';
import ClientUser from '@/model/entity/client/client-user';
import ClientUserSearchConditionsInterface from '@/model/vo/client/client-user-search-conditions-interface';
import ClientUserSearchResultsVo from '@/model/vo/client/client-user-search-results-vo';
import FormFieldOptionInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-option-interface';
import GetClientTask from '@/model/task/client/get-client-task';
import GetClientUserTask from '@/model/task/client/get-client-user-task';
import GetClientUsersTask from '@/model/task/client/get-client-users-task';
import GetClientsTask from '@/model/task/client/get-clients-task';
import PatchClientTask from '@/model/task/client/patch-client-task';
import PatchClientUserTask from '@/model/task/client/patch-client-user-task';
import PostClientTask from '@/model/task/client/post-client-task';
import PostClientUserTask from '@/model/task/client/post-client-user-task';

export default class ClientService extends AbstractAuthenticatedService
{
    /**
     * Returns matching clients
     *
     * @returns
     */

    public async findClients(searchConditions?: ClientSearchConditionsInterface): Promise<ClientSearchResultsVo>
    {
        const task = new GetClientsTask();
        task.token = this.token;
        task.searchConditions = searchConditions;

        return this.runTask(task) as Promise<ClientSearchResultsVo>;
    }

    /**
     * Loads a client
     *
     * @param id
     * @returns
     */

    public async loadClient(id: string): Promise<Client>
    {
        const task = new GetClientTask();
        task.token = this.token;
        task.id = id;

        return this.runTask(task) as Promise<Client>;
    }

    /**
     * Creates a client
     *
     * @param client
     * @returns
     */

    public async createClient(client: Client): Promise<Client>
    {
        const task = new PostClientTask();
        task.token = this.token;
        task.client = client;

        return this.runTask(task) as Promise<Client>;
    }

    /**
     * Updates a client
     *
     * @param client
     * @returns
     */

    public async updateClient(client: Client): Promise<Client>
    {
        const task = new PatchClientTask();
        task.token = this.token;
        task.client = client;

        return this.runTask(task) as Promise<Client>;
    }

    /**
     * Callback function for form options
     *
     * @param search
     * @param teamId
     * @returns
     */

    public async clientFormOptionsCallback(
        search: string,
        teamId: number|null = null,
        itemsPerPage: number|null = null,
    ): Promise<FormFieldOptionInterface[]>
    {
        const clients = await this.findClients({
            searchTerm: search,
            team: teamId ?? undefined,
            archived: false,
            disabled: false,
            itemsPerPage: itemsPerPage ?? undefined,
            order: [
                {
                    key: 'name',
                    descending: false,
                },
            ],
        });

        const options: FormFieldOptionInterface[] = [];

        for (const client of clients.results)
        {
            options.push({
                value: client.id,
                title: client.name,
            });
        }

        return options;
    }

    /**
     * Callback function for form titles
     *
     * @param values
     * @returns
     */

    public async clientFormTitlesCallback(values: string[]): Promise<Record<string, string>>
    {
        const clients = await this.findClients({
            id: values,
            order: [
                {
                    key: 'name',
                    descending: false,
                },
            ],
        });

        const options: Record<string, string> = {};

        for (const client of clients.results)
        {
            if (!client.id)
            {
                continue;
            }

            options[client.id] = client.name;
        }

        return options;
    }

    /**
     * Returns matching client users
     *
     * @returns
     */

    public async findClientUsers(searchConditions?: ClientUserSearchConditionsInterface): Promise<ClientUserSearchResultsVo>
    {
        const task = new GetClientUsersTask();
        task.token = this.token;
        task.searchConditions = searchConditions;

        return this.runTask(task) as Promise<ClientUserSearchResultsVo>;
    }

    /**
     * Loads a client user
     *
     * @param id
     * @returns
     */

    public async loadClientUser(id: string): Promise<ClientUser>
    {
        const task = new GetClientUserTask();
        task.token = this.token;
        task.id = id;

        return this.runTask(task) as Promise<ClientUser>;
    }

    /**
     * Creates a client user
     *
     * @param clientUser
     * @returns
     */

    public async createClientUser(clientUser: ClientUser): Promise<ClientUser>
    {
        const task = new PostClientUserTask();
        task.token = this.token;
        task.clientUser = clientUser;

        return this.runTask(task) as Promise<ClientUser>;
    }

    /**
     * Updates a clientUser
     *
     * @param clientUser
     * @returns
     */

    public async updateClientUser(clientUser: ClientUser): Promise<ClientUser>
    {
        const task = new PatchClientUserTask();
        task.token = this.token;
        task.clientUser = clientUser;

        return this.runTask(task) as Promise<ClientUser>;
    }

    /**
     * Callback function for client user form options
     *
     * @param search
     * @param clientId
     * @returns
     */

    public async clientUserFormOptionsCallback(search: string, clientId: number|null): Promise<FormFieldOptionInterface[]>
    {
        const clientUsers = await this.findClientUsers({
            searchTerm: search,
            client: clientId ? `${ clientId }` : undefined,
            disabled: false,
            order: [
                {
                    key: 'firstName',
                    descending: false,
                },
                {
                    key: 'lastName',
                    descending: false,
                },
            ],
        });

        const options: FormFieldOptionInterface[] = [];

        for (const clientUser of clientUsers.results)
        {
            options.push({
                value: clientUser.id,
                title: clientUser.fullName,
            });
        }

        return options;
    }

    /**
      * Callback function for client user form titles
      *
      * @param values
      * @param clientId
      * @returns
      */

    public async clientUserFormTitlesCallback(values: string[], clientId: number|null): Promise<Record<string, string>>
    {
        const clientUsers = await this.findClientUsers({
            id: values,
            client: clientId ? `${ clientId }` : undefined,
            order: [
                {
                    key: 'firstName',
                    descending: false,
                },
                {
                    key: 'lastName',
                    descending: false,
                },
            ],
        });

        const options: Record<string, string> = {};

        for (const clientUser of clientUsers.results)
        {
            if (!clientUser.id)
            {
                continue;
            }

            options[clientUser.id] = clientUser.fullName;
        }

        return options;
    }
}